import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatDate, formatDateTime, initials, whatsappHref } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ContatoRegistrarModal from "@/components/ContatoRegistrarModal";
import CartaoAniversarioModal from "@/components/CartaoAniversarioModal";
import AgendaRetornoDeepModal from "@/components/AgendaRetornoDeepModal";
import {
  CalendarClock, AlertTriangle, Sun, ChevronRight, Check, Phone, MessageCircle,
  Plus, CalendarPlus, Trash2, Cake, Gift, Trophy, PartyPopper, User as UserIcon,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

function Section({ titulo, icone: Ico, cor, items, onOpen, onConcluir, testid }) {
  return (
    <Card className="p-6 rounded-2xl" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Ico className={`w-5 h-5 ${cor}`} />
        <h2 className="font-display font-bold text-xl text-slate-900">{titulo}</h2>
        <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${cor.replace("text-", "bg-").replace("-600", "-100")} ${cor}`}>
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="text-slate-400 text-sm py-4">Nada por aqui 🎉</div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div
              key={c.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpen(c)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(c); } }}
              className="w-full text-left rounded-xl border border-slate-200 p-3 bg-white hover:shadow-md hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300 transition cursor-pointer"
              data-testid={`retorno-${c.id}`}
            >
              <div className="flex items-start gap-3">
                <Avatar className="w-9 h-9 flex-shrink-0">
                  <AvatarFallback className="text-xs bg-slate-100 text-slate-700 font-bold">
                    {initials(c.vendedora_nome)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900">{c.municipio_nome}</span>
                    <span className="text-xs text-slate-400">/ {c.municipio_uf}</span>
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${c.canal === "ligacao" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>
                      {c.canal === "ligacao" ? <><Phone className="w-3 h-3 inline mr-0.5"/>Lig.</> : <><MessageCircle className="w-3 h-3 inline mr-0.5"/>Zap</>}
                    </span>
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5">
                    <b>{c.pessoa_nome}</b> — {c.pessoa_funcao}
                  </div>
                  {c.observacao && (
                    <div className="text-sm text-slate-500 italic mt-1 line-clamp-2">&ldquo;{c.observacao}&rdquo;</div>
                  )}
                  <div className="text-xs text-slate-400 mt-1">
                    Retorno: <b className="text-slate-700">{formatDate(c.data_retorno + "T12:00:00")}</b>
                    <span className="ml-3">· vendedora: {c.vendedora_nome}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); onConcluir(c); }}
                    data-testid={`retorno-concluir-${c.id}`}
                    className="text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  >
                    <Check className="w-3 h-3 mr-1" /> Feito
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function EventoCard({ ev, onToggle, onDelete, canEdit = true }) {
  return (
    <div className={`rounded-xl border p-3 bg-white hover:shadow-sm transition ${ev.concluido ? "opacity-60 border-slate-200" : "border-blue-100"}`}
      data-testid={`evento-${ev.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center ${ev.concluido ? "bg-slate-100 text-slate-500" : "bg-blue-50 text-blue-700"}`}>
          <CalendarPlus className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-semibold ${ev.concluido ? "line-through text-slate-500" : "text-slate-900"}`}>{ev.titulo}</div>
          {ev.descricao && (
            <div className="text-sm text-slate-500 mt-0.5 whitespace-pre-wrap">{ev.descricao}</div>
          )}
          <div className="text-xs text-slate-400 mt-1">
            {formatDateTime(ev.data)}
            <span className="ml-2">· {ev.vendedora_nome}</span>
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-col gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onToggle(ev)}
              className={`text-xs ${ev.concluido ? "border-slate-200" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}
              data-testid={`evento-toggle-${ev.id}`}
            >
              <Check className="w-3 h-3 mr-1" /> {ev.concluido ? "Reabrir" : "Feito"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(ev)}
              className="text-xs border-red-200 text-red-600 hover:bg-red-50"
              data-testid={`evento-del-${ev.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/* Modal de Parabéns quando a agenda está zerada */
function ParabensAgendaModal({ open, onClose, userName }) {
  useEffect(() => {
    if (!open) return;
    // Confetti explosivo
    const duration = 2000;
    const end = Date.now() + duration;
    const colors = ["#10b981", "#3b82f6", "#f59e0b", "#a855f7", "#ef4444"];
    (function frame() {
      confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    confetti({ particleCount: 250, spread: 130, origin: { y: 0.35 }, colors });
    try {
      const audio = new Audio("https://cdn.jsdelivr.net/gh/anars/blank-audio@master/2-seconds-of-silence.mp3");
      audio.volume = 0.0; // silêncio; celebração é visual
      audio.play().catch(() => {});
    } catch (err) { console.warn("Agenda parabéns audio failed:", err?.message || err); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md text-center" data-testid="agenda-parabens-modal">
        <DialogHeader>
          <DialogTitle className="sr-only">Parabéns</DialogTitle>
        </DialogHeader>
        <div className="py-6 space-y-4">
          <div className="mx-auto w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center shadow-lg">
            <PartyPopper className="w-14 h-14 text-white" />
          </div>
          <div className="font-display text-3xl font-bold text-slate-900">Zerou a agenda!</div>
          <div className="text-slate-600">
            {userName ? <b>{userName}</b> : "Você"}, todos os retornos de hoje e atrasados foram concluídos.<br />
            Agora é <b className="text-emerald-600">100% ataque</b>: registre mais contatos e feche mais inscrições. 🚀
          </div>
          <Button onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700 mt-2" data-testid="agenda-parabens-fechar">
            Bora vender! 💪
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Agenda() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [aniv, setAniv] = useState({ hoje: [], proximos: [] });
  const [novoOpen, setNovoOpen] = useState(false);
  const [novo, setNovo] = useState({ titulo: "", data: "", descricao: "" });
  const [retornoAtivo, setRetornoAtivo] = useState(null);
  const [vendedoras, setVendedoras] = useState([]);
  const [filtroVend, setFiltroVend] = useState("todos");
  const [modoPessoal, setModoPessoal] = useState(false);
  const [parabensOpen, setParabensOpen] = useState(false);
  const [anivChoice, setAnivChoice] = useState(null); // pessoa (aniversariante) selecionado — abre modal escolha
  const [anivContatoModal, setAnivContatoModal] = useState(null); // { pessoa, canal }
  const [cartaoAniv, setCartaoAniv] = useState(null); // pessoa para gerar cartão
  const totalPendentesAntes = useRef(null);

  const canFilter = user?.role === "diretoria" || user?.role === "supervisao" || user?.role === "gestao_cel";
  const isGestaoCel = user?.role === "gestao_cel";

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (modoPessoal) {
      params.set("mine", "true");
    } else if (filtroVend && filtroVend !== "todos") {
      params.set("vendedora_id", filtroVend);
    }
    const q = params.toString() ? `?${params.toString()}` : "";

    // Retornos usam vendedora_id (não têm "mine", mas se modo pessoal, forçamos user.id)
    const retParams = new URLSearchParams();
    if (modoPessoal && user?.id) retParams.set("vendedora_id", user.id);
    else if (filtroVend && filtroVend !== "todos") retParams.set("vendedora_id", filtroVend);
    const rq = retParams.toString() ? `?${retParams.toString()}` : "";

    api.get(`/agenda/retornos${rq}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/eventos-agenda${q}`).then((r) => setEventos(r.data || [])).catch(() => {});
    api.get("/agenda/aniversarios").then((r) => setAniv(r.data || { hoje: [], proximos: [] })).catch(() => {});
  }, [modoPessoal, filtroVend, user?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (canFilter) {
      api.get("/users").then((r) => {
        let list = (r.data || []).filter((u) => u.role === "vendedora");
        // Iter 52: gestão CeL só vê agenda das vendedoras CeL
        if (isGestaoCel) {
          list = list.filter((u) => u.nucleo_nome === "CeL Consultoria");
        } else if (user?.nucleo_id) {
          list = list.filter((u) => u.nucleo_id === user.nucleo_id);
        }
        setVendedoras(list);
      }).catch(() => {});
    }
  }, [canFilter, isGestaoCel]);

  // Detecta transição "tinha pendências → zerou" para celebrar
  useEffect(() => {
    if (!data) return;
    const totalPend = data.atrasados.length + data.hoje.length;
    if (totalPendentesAntes.current === null) {
      // primeira medição
      totalPendentesAntes.current = totalPend;
      return;
    }
    if (totalPendentesAntes.current > 0 && totalPend === 0) {
      const hoje = new Date().toISOString().slice(0, 10);
      const key = `agenda-zerou-${user?.id}-${hoje}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        setParabensOpen(true);
      }
    }
    totalPendentesAntes.current = totalPend;
  }, [data, user?.id]);

  if (!data) return <div className="text-slate-500">Carregando agenda...</div>;

  const abrir = (c) => setRetornoAtivo(c);

  const concluir = async (c) => {
    try {
      await api.patch(`/contatos/${c.id}`, { retorno_concluido: true });
      toast.success("Retorno concluído");
      load();
    } catch { toast.error("Erro"); }
  };

  // Ao salvar novo contato pelo modal de retorno, marca o retorno original como concluído
  const onSalvouRetorno = async () => {
    if (!retornoAtivo) return;
    try {
      await api.patch(`/contatos/${retornoAtivo.id}`, { retorno_concluido: true });
    } catch { /* silent */ }
    setRetornoAtivo(null);
    toast.success("Retorno registrado e concluído ✓");
    load();
  };

  const criarEvento = async () => {
    if (!novo.titulo.trim() || !novo.data) {
      toast.error("Preencha título e data/hora");
      return;
    }
    try {
      await api.post("/eventos-agenda", {
        titulo: novo.titulo.trim(),
        data: novo.data,
        descricao: novo.descricao?.trim() || null,
      });
      toast.success("Evento criado");
      setNovo({ titulo: "", data: "", descricao: "" });
      setNovoOpen(false);
      load();
    } catch { toast.error("Erro ao criar evento"); }
  };

  const toggleEvento = async (ev) => {
    try {
      await api.patch(`/eventos-agenda/${ev.id}`, { concluido: !ev.concluido });
      load();
    } catch { toast.error("Erro"); }
  };

  const delEvento = async (ev) => {
    if (!window.confirm(`Excluir "${ev.titulo}"?`)) return;
    try {
      await api.delete(`/eventos-agenda/${ev.id}`);
      toast.success("Evento excluído");
      load();
    } catch { toast.error("Erro"); }
  };

  const totalPendentes = data.atrasados.length + data.hoje.length;
  const eventosPendentes = eventos.filter((e) => !e.concluido);
  const eventosConcluidos = eventos.filter((e) => e.concluido);

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CalendarClock className="w-6 h-6 text-blue-600" />
            <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">
              Agenda{modoPessoal ? " · minha" : ""}
            </h1>
          </div>
          <p className="text-slate-500 text-sm">
            {totalPendentes > 0
              ? <>Você tem <b className="text-red-600">{totalPendentes}</b> retorno{totalPendentes > 1 ? "s" : ""} pendente{totalPendentes > 1 ? "s" : ""} entre atrasados e hoje.</>
              : "Tudo em dia por aqui! Que tal registrar mais contatos e agendar retornos? 🚀"
            }
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {canFilter && (
            <>
              <Button
                variant={modoPessoal ? "default" : "outline"}
                onClick={() => { setModoPessoal((v) => !v); if (!modoPessoal) setFiltroVend("todos"); }}
                className={modoPessoal ? "bg-purple-600 hover:bg-purple-700" : ""}
                data-testid="btn-agenda-pessoal"
              >
                <UserIcon className="w-4 h-4 mr-1" /> Minha agenda
              </Button>
              {!modoPessoal && (
                <Select value={filtroVend} onValueChange={setFiltroVend}>
                  <SelectTrigger className="w-56" data-testid="filter-vendedora">
                    <SelectValue placeholder="Todas as vendedoras" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todas as vendedoras</SelectItem>
                    {vendedoras.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.codigo ? `${v.codigo} · ` : ""}{v.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </>
          )}
          <Button onClick={() => setNovoOpen(true)} data-testid="btn-novo-evento" className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-1" /> Novo evento
          </Button>
        </div>
      </div>

      {/* Eventos customizados */}
      <Card className="p-6 rounded-2xl" data-testid="section-eventos">
        <div className="flex items-center gap-2 mb-4">
          <CalendarPlus className="w-5 h-5 text-blue-600" />
          <h2 className="font-display font-bold text-xl text-slate-900">
            {modoPessoal ? "Meus eventos" : (filtroVend !== "todos" && canFilter ? "Eventos da vendedora" : "Eventos")}
          </h2>
          <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
            {eventosPendentes.length}
          </span>
        </div>
        {eventosPendentes.length === 0 && eventosConcluidos.length === 0 ? (
          <div className="text-slate-400 text-sm py-4">
            Nenhum evento ainda. Crie compromissos livres (reuniões, tarefas, lembretes) em "Novo evento".
          </div>
        ) : (
          <div className="space-y-2">
            {eventosPendentes.map((ev) => (
              <EventoCard
                key={ev.id}
                ev={ev}
                onToggle={toggleEvento}
                onDelete={delEvento}
                canEdit={ev.vendedora_id === user?.id || user?.role !== "vendedora"}
              />
            ))}
            {eventosConcluidos.length > 0 && (
              <details className="mt-3">
                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                  Ver {eventosConcluidos.length} concluído{eventosConcluidos.length > 1 ? "s" : ""}
                </summary>
                <div className="mt-2 space-y-2">
                  {eventosConcluidos.map((ev) => (
                    <EventoCard
                      key={ev.id}
                      ev={ev}
                      onToggle={toggleEvento}
                      onDelete={delEvento}
                      canEdit={ev.vendedora_id === user?.id || user?.role !== "vendedora"}
                    />
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </Card>

      {/* Aniversários — só HOJE (Iter 49) */}
      {aniv.hoje.length > 0 && (
        <Card className="p-6 rounded-2xl" data-testid="section-aniversarios">
          <div className="flex items-center gap-2 mb-4">
            <Cake className="w-5 h-5 text-pink-600" />
            <h2 className="font-display font-bold text-xl text-slate-900">Aniversariantes de hoje</h2>
            <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">
              {aniv.hoje.length}
            </span>
          </div>

          <div className="text-[11px] uppercase font-bold tracking-widest text-pink-600 mb-2 flex items-center gap-1">
            <Gift className="w-3 h-3" /> Faça contato de relacionamento!
          </div>
          <div className="space-y-2">
            {aniv.hoje.map((p) => (
              <div
                key={p.id}
                className={
                  "rounded-xl border-2 p-3 cursor-pointer hover:shadow-sm transition " +
                  (p.parabens_dado_hoje
                    ? "border-emerald-300 bg-gradient-to-r from-emerald-50 to-lime-50"
                    : "border-pink-200 bg-gradient-to-r from-pink-50 to-rose-50")
                }
                onClick={() => setAnivChoice(p)}
                data-testid={`aniv-hoje-${p.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={
                    "w-10 h-10 rounded-lg text-white flex items-center justify-center flex-shrink-0 " +
                    (p.parabens_dado_hoje ? "bg-emerald-500" : "bg-pink-500")
                  }>
                    {p.parabens_dado_hoje ? <Check className="w-5 h-5" /> : <Cake className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                      🎂 {p.nome}
                      {p.idade && <span className="text-slate-500 font-normal text-sm">· {p.idade} anos</span>}
                      {p.parabens_dado_hoje && (
                        <span className="text-[10px] uppercase font-bold tracking-widest bg-emerald-500 text-white px-1.5 py-0.5 rounded flex items-center gap-1" data-testid={`aniv-check-${p.id}`}>
                          <Check className="w-3 h-3" /> Parabéns dado
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-600">
                      {p.funcao} · <b>{p.municipio_nome}</b>
                      <span className="text-slate-400 text-xs ml-1">/ {p.municipio_uf}</span>
                    </div>
                    {/* Iter 49: badge da vendedora (só para não-vendedoras — diretor/supervisão) */}
                    {user?.role !== "vendedora" && p.vendedora_nome && (
                      <div className="text-[11px] mt-1 inline-flex items-center gap-1 bg-white/70 border border-pink-200 rounded px-1.5 py-0.5 text-slate-700" data-testid={`aniv-vend-${p.id}`}>
                        <UserIcon className="w-3 h-3 text-pink-500" />
                        <span className="font-semibold">{p.vendedora_nome}</span>
                        {p.vendedora_codigo && <span className="text-slate-400">· {p.vendedora_codigo}</span>}
                      </div>
                    )}
                    {p.telefone && (
                      <a
                        href={whatsappHref(p.telefone)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-emerald-700 hover:underline mt-0.5 inline-flex items-center gap-1 font-medium ml-2"
                        data-testid={`aniv-tel-wa-${p.pessoa_id || p.id}`}
                        title="Abrir no WhatsApp Web"
                      >
                        💬 {p.telefone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Estado zerado — quando não há pendências, um bloco motivacional */}
      {totalPendentes === 0 && data.proximos.length === 0 && (
        <Card className="p-8 rounded-2xl text-center bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200" data-testid="agenda-zerada">
          <Trophy className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <div className="font-display text-2xl font-bold text-slate-900 mb-1">Sua agenda tá zerada!</div>
          <div className="text-slate-600 text-sm">Aproveita para prospectar novos municípios e agendar retornos. 🔥</div>
        </Card>
      )}

      <Section
        titulo="Retornos atrasados"
        icone={AlertTriangle}
        cor="text-red-600"
        items={data.atrasados}
        onOpen={abrir}
        onConcluir={concluir}
        testid="section-atrasados"
      />
      <Section
        titulo="Retornos de hoje"
        icone={Sun}
        cor="text-amber-600"
        items={data.hoje}
        onOpen={abrir}
        onConcluir={concluir}
        testid="section-hoje"
      />
      <Section
        titulo="Próximos 2 dias"
        icone={CalendarClock}
        cor="text-blue-600"
        items={data.proximos.filter((c) => {
          const d = new Date(c.data_retorno + "T12:00:00");
          const in2 = new Date();
          in2.setDate(in2.getDate() + 2);
          return d <= in2;
        })}
        onOpen={abrir}
        onConcluir={concluir}
        testid="section-proximos"
      />

      {/* Modal novo evento */}
      <Dialog open={novoOpen} onOpenChange={setNovoOpen}>
        <DialogContent data-testid="novo-evento-modal">
          <DialogHeader>
            <DialogTitle>Novo evento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Título*</label>
              <Input
                value={novo.titulo}
                onChange={(e) => setNovo({ ...novo, titulo: e.target.value })}
                placeholder="Ex.: Reunião com prefeito"
                data-testid="input-evento-titulo"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Data e hora*</label>
              <Input
                type="datetime-local"
                value={novo.data}
                onChange={(e) => setNovo({ ...novo, data: e.target.value })}
                data-testid="input-evento-data"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Descrição</label>
              <Textarea
                value={novo.descricao}
                onChange={(e) => setNovo({ ...novo, descricao: e.target.value })}
                placeholder="Detalhes opcionais..."
                rows={3}
                data-testid="input-evento-desc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovoOpen(false)}>Cancelar</Button>
            <Button onClick={criarEvento} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-salvar-evento">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de parabéns */}
      <ParabensAgendaModal
        open={parabensOpen}
        onClose={() => setParabensOpen(false)}
        userName={user?.nome}
      />

      {/* Modal profundo para retorno — Iter 42 (item #7): mostra dados de contato,
          registra follow-up, e permite mover o card do funil sem duplicar */}
      {retornoAtivo && (
        <AgendaRetornoDeepModal
          open={!!retornoAtivo}
          onClose={() => setRetornoAtivo(null)}
          onDone={() => { setRetornoAtivo(null); load(); }}
          retorno={retornoAtivo}
        />
      )}

      {/* Aniversariante — modal de escolha (registrar contato / gerar cartão) */}
      <Dialog open={!!anivChoice} onOpenChange={(v) => !v && setAnivChoice(null)}>
        <DialogContent data-testid="modal-aniv-escolha">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cake className="w-5 h-5 text-pink-500" />
              Aniversariante · {anivChoice?.nome}
            </DialogTitle>
          </DialogHeader>
          {anivChoice && (
            <div className="text-sm text-slate-600 mb-3">
              {anivChoice.funcao} · <b>{anivChoice.municipio_nome}</b>
              {anivChoice.idade && <> · {anivChoice.idade} anos</>}
              {anivChoice.parabens_dado_hoje && (
                <span className="ml-2 text-[10px] uppercase font-bold tracking-widest bg-emerald-500 text-white px-1.5 py-0.5 rounded">
                  <Check className="w-3 h-3 inline -mt-0.5" /> Contato já registrado hoje
                </span>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Button
              onClick={() => {
                setAnivContatoModal({ pessoa: anivChoice, canal: "whatsapp" });
                setAnivChoice(null);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-auto py-3 flex flex-col gap-1"
              data-testid="btn-aniv-registrar-wa"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Registrar WhatsApp</span>
            </Button>
            <Button
              onClick={() => {
                setAnivContatoModal({ pessoa: anivChoice, canal: "ligacao" });
                setAnivChoice(null);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white h-auto py-3 flex flex-col gap-1"
              data-testid="btn-aniv-registrar-lig"
            >
              <Phone className="w-5 h-5" />
              <span>Registrar Ligação</span>
            </Button>
            <Button
              onClick={() => {
                setCartaoAniv(anivChoice);
                setAnivChoice(null);
              }}
              className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white h-auto py-3 flex flex-col gap-1"
              data-testid="btn-aniv-gerar-cartao"
            >
              <Sparkles className="w-5 h-5" />
              <span>Gerar Cartão 🎂</span>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAnivChoice(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Registrar contato do aniversariante */}
      {anivContatoModal && (
        <ContatoRegistrarModal
          open={!!anivContatoModal}
          onClose={() => setAnivContatoModal(null)}
          onSaved={() => {
            setAnivContatoModal(null);
            toast.success("Contato registrado! 🎉");
            load(); // recarrega para atualizar a marca ✓
          }}
          pessoa={anivContatoModal.pessoa}
          canal={anivContatoModal.canal}
          municipioId={anivContatoModal.pessoa?.municipio_id}
        />
      )}

      {/* Cartão de aniversário gerado por canvas */}
      <CartaoAniversarioModal
        open={!!cartaoAniv}
        onClose={() => setCartaoAniv(null)}
        pessoa={cartaoAniv}
      />
    </div>
  );
}
